import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

/**
 * Dialog "Nouveau bénéficiaire" déclenché depuis le formulaire de réservation.
 * Reprend exactement les mêmes champs que la page Bénéficiaires (ClientsPage),
 * pour qu'une fiche créée depuis ce flux soit aussi complète qu'une fiche
 * créée depuis la page principale.
 */
export function NewClientDialog({
  open,
  onOpenChange,
  clientData,
  setClientData,
  onSave,
  saving,
}) {
  const update = (patch) => setClientData({ ...clientData, ...patch });
  const updateContact = (patch) =>
    setClientData({ ...clientData, contact: { ...(clientData.contact || {}), ...patch } });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nouveau bénéficiaire</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Nom / Prénom *</Label>
            <Input
              value={clientData.name || ''}
              onChange={(e) => update({ name: e.target.value })}
              placeholder="Jean Dupont"
              data-testid="new-client-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Type *</Label>
            <Select
              value={clientData.client_type}
              onValueChange={(value) => update({ client_type: value })}
            >
              <SelectTrigger data-testid="new-client-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="diocese">Diocèse</SelectItem>
                <SelectItem value="paroisse">Paroisse</SelectItem>
                <SelectItem value="eglise">Église (mouvements, associations)</SelectItem>
                <SelectItem value="association_ig">Association d'intérêt général</SelectItem>
                <SelectItem value="entreprise">Entreprise / Fédération Pro / Asso +50 sal.</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <input
              type="checkbox"
              id="new-client-convention"
              checked={!!clientData.convention}
              onChange={(e) => update({ convention: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300"
              data-testid="new-client-convention-checkbox"
            />
            <Label htmlFor="new-client-convention" className="text-sm font-medium text-blue-800 cursor-pointer">
              Convention avec le Diocèse (accès salles sans surcoût)
            </Label>
          </div>

          <div className="space-y-2">
            <Label>Raison sociale</Label>
            <Input
              value={clientData.company_name || ''}
              onChange={(e) => update({ company_name: e.target.value })}
              placeholder="Nom de l'organisation"
              data-testid="new-client-company-input"
            />
          </div>

          <div className="space-y-2">
            <Label>SIRET <span className="text-slate-400">(facultatif)</span></Label>
            <Input
              value={clientData.siret || ''}
              onChange={(e) => update({ siret: e.target.value })}
              placeholder="123 456 789 00012"
              maxLength={17}
              data-testid="new-client-siret-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Adresse</Label>
            <Input
              value={clientData.address || ''}
              onChange={(e) => update({ address: e.target.value })}
              placeholder="12 rue de l'Église, 09100 Pamiers"
              data-testid="new-client-address-input"
            />
          </div>

          <div className="border-t pt-4">
            <Label className="text-base font-semibold mb-3 block">Contact principal</Label>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Nom du contact *</Label>
                <Input
                  value={clientData.contact?.name || ''}
                  onChange={(e) => updateContact({ name: e.target.value })}
                  placeholder="Jean Dupont"
                  data-testid="new-contact-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={clientData.contact?.email || ''}
                    onChange={(e) => updateContact({ email: e.target.value })}
                    placeholder="contact@organisation.fr"
                    data-testid="new-contact-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone *</Label>
                  <Input
                    value={clientData.contact?.phone || ''}
                    onChange={(e) => updateContact({ phone: e.target.value })}
                    placeholder="06 12 34 56 78"
                    data-testid="new-contact-phone-input"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={clientData.notes || ''}
              onChange={(e) => update({ notes: e.target.value })}
              placeholder="Notes internes..."
              rows={2}
              data-testid="new-client-notes-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            onClick={onSave}
            disabled={saving}
            className="bg-[#1e3a8a] hover:bg-[#1e40af]"
            data-testid="save-new-client-button"
          >
            {saving ? 'Création...' : 'Créer le bénéficiaire'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
